import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/axiosInstance";

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    coverImage: "",
    seo: {
      metaTitle: "",
      metaDescription: "",
    },
    social: {
      linkedinText: "",
      instagramText: "",
      hashtags: [],
    },
    isPublished: false,
    isFeatured: false,
  });

  /* ================= LOAD BLOG ================= */
  useEffect(() => {
    if (id) {
      console.log("BlogForm - Loading blog with ID:", id);
      api.get(`/api/blogs/${id}`)
        .then((res) => {
          console.log("BlogForm - API response:", res.data);
          const blogData = res.data.data || res.data;
          
          // Ensure seo and social fields exist
          const formData = {
            ...blogData,
            seo: {
              metaTitle: blogData.seo?.metaTitle || "",
              metaDescription: blogData.seo?.metaDescription || "",
            },
            social: {
              linkedinText: blogData.social?.linkedinText || "",
              instagramText: blogData.social?.instagramText || "",
              hashtags: blogData.social?.hashtags || [],
            },
          };
          
          setForm(formData);
        })
        .catch((error) => {
          console.error("BlogForm - Error loading blog:", error);
          alert("Failed to load blog data");
        });
    }
  }, [id]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("FORM DATA BEING SENT:", form); // Debug line

    if (!form.title) {
      alert("Title is required");
      return;
    }

    try {
      if (id) {
        await api.put(`/api/blogs/${id}`, form);
      } else {
        await api.post("/api/blogs", form);
      }

      navigate("/admin/blogs");
    } catch (error) {
      console.error("BLOG SUBMIT ERROR:", error.response?.data);
      console.error("BLOG SUBMIT STATUS:", error.response?.status);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  /* ================= HASHTAG GENERATOR ================= */
  const generateHashtags = () => {
    const text = `${form.title} ${form.content}`;
    const words = text
      .toLowerCase()
      .replace(/[^a-zA-Z ]/g, "")
      .split(" ")
      .filter((w) => w.length > 4);

    const unique = [...new Set(words)].slice(0, 8);
    setForm({
      ...form,
      social: {
        ...form.social,
        hashtags: unique,
      },
    });
  };

  /* ================= COPY LINKEDIN ================= */
  const copyLinkedInPost = () => {
    const text = `${form.social.linkedinText}\n\n${form.social.hashtags
      .map((h) => `#${h}`)
      .join(" ")}`;

    navigator.clipboard.writeText(text);
    alert("LinkedIn post copied!");
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-5xl mx-auto space-y-6 pb-28"
    >
      {/* ================= HEADER ================= */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-2xl font-bold">
          {id ? "Edit Blog" : "Create New Blog"}
        </h2>
      </div>

      {/* ================= CONTENT EDITOR ================= */}
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <input
          className="w-full border p-3 rounded-lg text-lg font-medium"
          placeholder="Blog title"
          value={form.title}
          onChange={(e) =>
            setForm({ ...form, title: e.target.value })
          }
        />

        <textarea
          className="w-full border p-3 rounded-lg"
          placeholder="Excerpt (short summary)"
          rows={2}
          value={form.excerpt}
          onChange={(e) =>
            setForm({ ...form, excerpt: e.target.value })
          }
        />

        {/* Content Textarea */}
        <textarea
          className="w-full border p-4 rounded-lg min-h-[260px] focus:outline-none resize-none"
          placeholder="Write your blog here..."
          value={form.content}
          onChange={(e) =>
            setForm({
              ...form,
              content: e.target.value,
            })
          }
        />
      </div>

      {/* ================= MEDIA & STATUS ================= */}
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <input
          className="w-full border p-3 rounded-lg"
          placeholder="Cover image URL"
          value={form.coverImage}
          onChange={(e) =>
            setForm({ ...form, coverImage: e.target.value })
          }
        />

        <div className="flex gap-8">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) =>
                setForm({
                  ...form,
                  isPublished: e.target.checked,
                })
              }
            />
            Publish
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.isFeatured}
              onChange={(e) =>
                setForm({
                  ...form,
                  isFeatured: e.target.checked,
                })
              }
            />
            Featured
          </label>
        </div>
      </div>

      {/* ================= SEO ================= */}
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <h3 className="font-semibold text-lg">SEO</h3>

        <input
          className="w-full border p-3 rounded-lg"
          placeholder="Meta Title"
          value={form.seo.metaTitle}
          onChange={(e) =>
            setForm({
              ...form,
              seo: { ...form.seo, metaTitle: e.target.value },
            })
          }
        />

        <textarea
          className="w-full border p-3 rounded-lg"
          rows={2}
          placeholder="Meta Description"
          value={form.seo.metaDescription}
          onChange={(e) =>
            setForm({
              ...form,
              seo: {
                ...form.seo,
                metaDescription: e.target.value,
              },
            })
          }
        />
      </div>

      {/* ================= SOCIAL ================= */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow p-6 space-y-4">
        <h3 className="font-semibold text-lg">
          LinkedIn / Instagram
        </h3>

        <textarea
          className="w-full border p-3 rounded-lg bg-white"
          rows={4}
          placeholder="LinkedIn post content"
          value={form.social.linkedinText}
          onChange={(e) =>
            setForm({
              ...form,
              social: {
                ...form.social,
                linkedinText: e.target.value,
              },
            })
          }
        />

        {/* HASHTAGS */}
        <div className="flex gap-3 flex-wrap">
          {form.social.hashtags.map((tag, i) => (
            <span
              key={i}
              className="bg-white px-3 py-1 rounded-full text-sm border"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={generateHashtags}
            className="px-4 py-2 bg-gray-900 text-white rounded"
          >
            Auto Generate Hashtags
          </button>

          <button
            type="button"
            onClick={copyLinkedInPost}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Copy LinkedIn Post
          </button>
        </div>
      </div>

      {/* ================= LINKEDIN PREVIEW ================= */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold mb-3">LinkedIn Preview</h3>

        <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
          <p className="whitespace-pre-line">
            {form.social.linkedinText || "Your LinkedIn post preview..."}
          </p>
          <p className="text-blue-700 text-sm">
            {form.social.hashtags.map((h) => `#${h} `)}
          </p>
        </div>
      </div>

      {/* ================= STICKY SAVE ================= */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex justify-end">
        <button
          type="submit"
          className="px-8 py-3 bg-primary text-white rounded-lg text-lg shadow"
        >
          Save Blog
        </button>
      </div>
    </form>
  );
};

export default BlogForm;
